import {
  ActivityAvailabilitySummary,
  ActivityDetail,
  ActivityStatus,
  Activity as ApiActivity,
  ActivityFeatured as ApiActivityFeatured,
  ActivityLocationDraft as ApiActivityLocationDraft,
  ActivitySearchResult as ApiActivitySearchResult,
  CreateActivityBody,
  GetActivitiesAdminStatusFilter,
  GetActivitiesFeaturedParams,
  GetActivitiesParams,
  GetProfessionalActivitiesParams,
  ImageEdits,
  ImproveActivityDescriptionBodyLocale,
  NearestActivityLocation,
  ProfessionalActivity,
  ProfessionalActivityList,
  UpdateActivityBody
} from '@api/models';

import { ApiClient } from '@website/lib/api';
import { browserFetch } from '@website/lib/api/browserFetch';
import { getAssetUrl } from '@website/utils/assets';
import { calculateBookingsSinceLastYear, dateFromMixedTimestamp } from '@website/utils/date';
import { getTranslation } from '@website/utils/i18n';

import type { Professional } from './professionals.service';
import { getSession } from './session.service';

export interface ActivityRatingData {
  ratingAverage: number;
  ratingTotal: number;
  ratingDist: [number, number, number, number, number];
}

export interface Activity {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  mediaUrls?: string[];
  mediaKeys?: string[];
  /** Non-destructive crop metadata, keyed by the displayed (cropped) media key. */
  imageEdits?: ImageEdits;
  /** Localized disclaimer/"conditions générales" the pro attached (optional). */
  disclaimer?: string;
  professionalName: string;
  professionalId?: string;
  professionalEmail?: string;
  professionalPhone?: string;
  professional?: Professional;
  isAtHome?: boolean;
  homeRadius?: number;
  /** Bookable locations. The canonical 3-value kind (venue | at_home | online);
   *  online present iff the pro added it. */
  locations?: {
    id: string;
    kind: 'venue' | 'at_home' | 'online';
    label?: string;
    isActive: boolean;
    meetingProvider?: 'toptoptime' | 'byo';
    meetingUrl?: string;
  }[];
  /**
   * Read-only DERIVED mix of the distinct location kinds the activity offers
   * (venue / at_home / online). A mixed activity legitimately lists more than one;
   * surfaces the real delivery set so headers/badges no longer collapse to a
   * single `isAtHome` boolean.
   */
  locationKinds?: ('venue' | 'at_home' | 'online')[];
  /** Convenience: whether the activity has an active online location. */
  onlineEnabled?: boolean;
  /** Convenience: the active online location's meeting source + BYO url. */
  meetingProvider?: 'toptoptime' | 'byo';
  meetingUrl?: string;
  /**
   * IANA timezone of the activity's (venue) location, derived server-side from
   * its coordinates. Slot/booking wall-clock times are stored anchored to this
   * zone; authoring forms convert the pro's typed browser time into it on save.
   */
  timezone?: string;
  /**
   * Starting price (MIN across active slot time windows).
   * `null` means the activity has no priced active slots yet; this is
   * distinct from a genuinely free activity (`0`).
   */
  startingPrice: number | null;
  /**
   * Date→starting-price step schedule (sorted by `from`, `YYYY-MM-DD`), baked at
   * render time so the static page can resolve "today" client-side without
   * per-day revalidation. `null` when not yet computed (fall back to scalar).
   */
  startingPriceSchedule?: { from: string; price: number | null }[] | null;
  completedBookingsCount: number;
  bookingsSinceLastYear?: number;
  location: {
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  /**
   * The activity's location SET for the map (slot-level-locations, Section 4 C1/C2).
   * One entry per DISTINCT bookable slot location. Search returns the NEAREST
   * matching location; until the backend returns the full per-slot set this is
   * the nearest location (plus the activity coords as a fallback), de-duplicated
   * by coordinate. The LIST still shows one row per activity (F10): these drive
   * map PINS, not list rows. Empty for an at-home / coordinate-less activity.
   */
  locationSet?: {
    id: string;
    kind: 'venue' | 'at_home' | 'online';
    label?: string;
    address?: string;
    latitude: number;
    longitude: number;
    radius?: number;
    isAtHome: boolean;
  }[];
  category?: string;
  activityName?: string;
  activityImage?: string;
  singleSlot?: any;
  groupSlot?: any;
  slots?: any[];
  status: ActivityStatus;
  createdAt?: number;
  rating?: ActivityRatingData;
  /** Admin-applied audience scope override. Empty = follows owner's groups. */
  scopes?: string[];
}

export type ActivityBookingData = Activity;

export interface GetActivitiesResult {
  activities: Activity[];
  cursor: string | null;
  total: number;
  fallbackActivities?: Activity[];
  /** 'keyword' = same keyword search without the location filter (other areas); 'popular' = global top-booked. */
  fallbackKind?: 'keyword' | 'popular';
}

/**
 * Build an activity's map location SET from a search result (slot-level-locations).
 * Prefers the search-provided `nearestLocation` (the nearest bookable slot
 * location, slice 4); falls back to the activity's own coords. De-duplicates by
 * rounded coordinate so the same place is one pin. At-home and coordinate-less
 * activities yield an empty set (rendered as a coverage area / list-only).
 */
function buildActivityLocationSet(
  activity: ApiActivitySearchResult,
  isAtHome: boolean | undefined
): Activity['locationSet'] {
  const out: NonNullable<Activity['locationSet']> = [];
  const seen = new Set<string>();
  const push = (loc: {
    id: string;
    kind: 'venue' | 'at_home' | 'online';
    label?: string;
    address?: string;
    latitude: number;
    longitude: number;
    radius?: number;
    isAtHome: boolean;
  }) => {
    if (!Number.isFinite(loc.latitude) || !Number.isFinite(loc.longitude)) return;
    if (loc.latitude === 0 && loc.longitude === 0) return;
    const key = `${loc.latitude.toFixed(5)},${loc.longitude.toFixed(5)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(loc);
  };

  const nearest: NearestActivityLocation | undefined = activity.nearestLocation;
  if (nearest && nearest.latitude != null && nearest.longitude != null) {
    const kind = (nearest.kind as 'venue' | 'at_home' | 'online' | undefined) ?? (isAtHome ? 'at_home' : 'venue');
    push({
      id: nearest.id ?? `${activity.id}-nearest`,
      kind,
      label: nearest.label ?? undefined,
      address: nearest.addressLine1 ?? undefined,
      latitude: nearest.latitude,
      longitude: nearest.longitude,
      // `locationSet[].radius` is the at_home COVERAGE radius in KM. The API's
      // nearestLocation.radius is raw METERS, and only at_home has a meaningful
      // travel radius (a venue can carry a stale one), so normalize + gate on kind
      // to match the flat `homeRadius` projection (transform.ts).
      radius: kind === 'at_home' && nearest.radius != null ? nearest.radius / 1000 : undefined,
      isAtHome: kind === 'at_home'
    });
  }

  if (activity.latitude != null && activity.longitude != null) {
    push({
      id: `${activity.id}-activity`,
      kind: isAtHome ? 'at_home' : 'venue',
      address: activity.locationAddress ?? undefined,
      latitude: activity.latitude,
      longitude: activity.longitude,
      // homeRadius is already KM and at_home-gated by the API; keep the radius only
      // on the at_home coverage pin so the contract unit stays consistent.
      radius: isAtHome === true ? (activity.homeRadius ?? undefined) : undefined,
      isAtHome: isAtHome === true
    });
  }

  return out;
}

export const getActivities = async (
  params: GetActivitiesParams & { locale?: string }
): Promise<GetActivitiesResult> => {
  const { locale = 'en', ...restParams } = params;

  try {
    const apiParams: GetActivitiesParams = { ...restParams };
    const apiActivitiesResponse = await ApiClient.getActivities(apiParams);

    if (!apiActivitiesResponse || typeof apiActivitiesResponse !== 'object') {
      return { activities: [], cursor: null, total: 0 };
    }

    const activitiesData = Array.isArray(apiActivitiesResponse)
      ? apiActivitiesResponse
      : (apiActivitiesResponse as any).data || [apiActivitiesResponse];

    const cursor = (apiActivitiesResponse as any).cursor ?? null;
    const total = (apiActivitiesResponse as any).total ?? activitiesData.length;

    const activities = activitiesData.map((activity: ApiActivitySearchResult) => {
      const titleObj: { [key: string]: string | undefined } = {};
      if (activity.title) {
        if (typeof activity.title === 'string') {
          titleObj.en = activity.title;
        } else if (typeof activity.title === 'object') {
          Object.entries(activity.title).forEach(([key, value]) => {
            titleObj[key] = value as string;
          });
        }
      }

      const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
      const imageUrl = getAssetUrl(activity.mainImageKey);

      const firstName = activity.professionalName?.firstName || '';
      const lastName = activity.professionalName?.lastName || '';
      const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

      const ratingData: ActivityRatingData | undefined =
        activity.ratingTotal != null && activity.ratingTotal > 0
          ? {
              ratingAverage: activity.ratingAverage ?? 0,
              ratingTotal: activity.ratingTotal,
              ratingDist: [
                activity.ratingDist1 ?? 0,
                activity.ratingDist2 ?? 0,
                activity.ratingDist3 ?? 0,
                activity.ratingDist4 ?? 0,
                activity.ratingDist5 ?? 0
              ]
            }
          : undefined;

      const isAtHome = activity.isAtHome === null ? undefined : activity.isAtHome;
      const locationSet = buildActivityLocationSet(activity, isAtHome);

      return {
        id: activity.id,
        title: translatedTitle,
        imageUrl,
        professionalName,
        professionalId: activity.professionalId,
        isAtHome,
        locationKinds: activity.locationKinds,
        homeRadius: activity.homeRadius,
        // TODO(tz): expose timezone on getActivities (search) response to enable browser-time conversion here
        startingPrice: (activity.startingPrice as number | null) ?? null,
        completedBookingsCount: activity.completedBookingsCount || 0,
        bookingsSinceLastYear: activity.slots ? calculateBookingsSinceLastYear(activity.slots) : 0,
        location: {
          address: activity.locationAddress || 'Location not specified',
          coordinates: {
            latitude: activity.latitude || 0,
            longitude: activity.longitude || 0
          }
        },
        locationSet,
        category: activity.categoryId,
        status: activity.status,
        createdAt: activity.createdAt,
        rating: ratingData
      };
    });

    // Transform fallback data if present (uses same transform as primary results)
    const fallbackRaw = (apiActivitiesResponse as any).fallbackData;
    let fallbackActivities: Activity[] | undefined;
    if (Array.isArray(fallbackRaw) && fallbackRaw.length > 0) {
      fallbackActivities = fallbackRaw.map((activity: ApiActivitySearchResult) => {
        const fbTitleObj: { [key: string]: string | undefined } = {};
        if (activity.title) {
          if (typeof activity.title === 'string') {
            fbTitleObj.en = activity.title;
          } else if (typeof activity.title === 'object') {
            Object.entries(activity.title).forEach(([key, value]) => {
              fbTitleObj[key] = value as string;
            });
          }
        }
        const fbTitle = getTranslation(fbTitleObj, locale, 'Untitled Activity');
        const fbImageUrl = getAssetUrl(activity.mainImageKey);
        const fbFirstName = activity.professionalName?.firstName || '';
        const fbLastName = activity.professionalName?.lastName || '';
        const fbProfessionalName = [fbFirstName, fbLastName].filter(Boolean).join(' ') || 'Unknown Professional';

        const fbRating: ActivityRatingData | undefined =
          activity.ratingTotal != null && activity.ratingTotal > 0
            ? {
                ratingAverage: activity.ratingAverage ?? 0,
                ratingTotal: activity.ratingTotal,
                ratingDist: [
                  activity.ratingDist1 ?? 0,
                  activity.ratingDist2 ?? 0,
                  activity.ratingDist3 ?? 0,
                  activity.ratingDist4 ?? 0,
                  activity.ratingDist5 ?? 0
                ]
              }
            : undefined;

        return {
          id: activity.id,
          title: fbTitle,
          description: '',
          imageUrl: fbImageUrl,
          professionalName: fbProfessionalName,
          professionalId: activity.professionalId,
          isAtHome: activity.isAtHome === null ? undefined : activity.isAtHome,
          locationKinds: activity.locationKinds,
          homeRadius: activity.homeRadius,
          // TODO(tz): expose timezone on getActivities (search) response to enable browser-time conversion here
          startingPrice: (activity.startingPrice as number | null) ?? null,
          completedBookingsCount: activity.completedBookingsCount || 0,
          bookingsSinceLastYear: activity.slots ? calculateBookingsSinceLastYear(activity.slots) : 0,
          location: {
            address: activity.locationAddress || 'Location not specified',
            coordinates: {
              latitude: activity.latitude || 0,
              longitude: activity.longitude || 0
            }
          },
          category: activity.categoryId,
          status: activity.status,
          createdAt: activity.createdAt,
          rating: fbRating
        };
      });
    }

    const fallbackKind = (apiActivitiesResponse as any).fallbackKind as 'keyword' | 'popular' | undefined;

    return { activities, cursor, total, fallbackActivities, fallbackKind };
  } catch (error) {
    console.error('Failed to fetch activities:', error);
    return { activities: [], cursor: null, total: 0 };
  }
};

export const getActivitiesFeatured = async (params: {
  locale?: string;
  latitude?: number | null;
  longitude?: number | null;
  limit?: number;
  cursor?: string;
}): Promise<Activity[]> => {
  const { locale = 'en', latitude, longitude, limit, cursor } = params;

  try {
    const apiParams: GetActivitiesFeaturedParams = { latitude, longitude, limit, cursor };
    const apiActivitiesResponse = await ApiClient.getActivitiesFeatured(apiParams);

    if (!apiActivitiesResponse || typeof apiActivitiesResponse !== 'object') {
      return [];
    }

    const activitiesData = Array.isArray(apiActivitiesResponse)
      ? apiActivitiesResponse
      : (apiActivitiesResponse as any).data || [apiActivitiesResponse];

    return activitiesData.map((activity: ApiActivityFeatured) => {
      const titleObj: { [key: string]: string | undefined } = {};
      if (activity.title) {
        if (typeof activity.title === 'string') {
          titleObj.en = activity.title;
        } else if (typeof activity.title === 'object') {
          Object.entries(activity.title).forEach(([key, value]) => {
            titleObj[key] = value as string;
          });
        }
      }

      const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
      const imageUrl = getAssetUrl(activity.mainImageKey);

      const firstName = activity.professionalName?.firstName || '';
      const lastName = activity.professionalName?.lastName || '';
      const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

      return {
        id: activity.id,
        title: translatedTitle,
        imageUrl,
        professionalName,
        professionalId: (activity as any).professionalId || (activity as any).professional?.id || activity.id,
        isAtHome: activity.isAtHome === null ? undefined : activity.isAtHome,
        locationKinds: activity.locationKinds,
        homeRadius: (activity as any).homeRadius,
        // TODO(tz): expose timezone on getActivitiesFeatured response to enable browser-time conversion here
        startingPrice: (activity.startingPrice as number | null) ?? null,
        completedBookingsCount: 0,
        bookingsSinceLastYear: (activity as any).slots ? calculateBookingsSinceLastYear((activity as any).slots) : 0,
        location: {
          address: activity.locationAddress || 'Location not specified',
          coordinates: {
            latitude: activity.latitude || 0,
            longitude: activity.longitude || 0
          }
        },
        category: (activity as any).categoryId,
        status: activity.status
      };
    });
  } catch (error) {
    console.error('Failed to fetch featured activities:', error);
    return [];
  }
};

export const getActivitiesByProfessional = async (
  professionalId: string,
  params: GetProfessionalActivitiesParams & { locale?: string } = {}
): Promise<Activity[]> => {
  const { locale = 'en', statusFilter, ...restParams } = params;

  try {
    // Fetch ALL pages of activities. Backend defaults to a small page size (10)
    // and exposes max 100 per page, so we loop on the cursor until exhausted.
    const PAGE_LIMIT = 100;
    const activitiesData: ProfessionalActivity[] = [];
    let cursor: string | undefined = (restParams as any).cursor;

    do {
      const apiParams: GetProfessionalActivitiesParams = {
        ...restParams,
        limit: PAGE_LIMIT,
        ...(cursor ? { cursor } : {})
      } as GetProfessionalActivitiesParams;
      if (statusFilter) {
        apiParams.statusFilter = statusFilter;
      }
      const apiResponse: ProfessionalActivityList = await ApiClient.getProfessionalActivities(
        professionalId,
        apiParams
      );

      if (!apiResponse || typeof apiResponse !== 'object') {
        break;
      }

      const pageData = Array.isArray(apiResponse) ? apiResponse : (apiResponse as any).data || [apiResponse];
      activitiesData.push(...pageData);
      cursor = (apiResponse as any).cursor || undefined;
    } while (cursor);

    return activitiesData.map((activity: ProfessionalActivity) => {
      const titleObj: { [key: string]: string | undefined } = {};
      if (activity.title) {
        if (typeof activity.title === 'string') {
          titleObj.en = activity.title;
        } else if (typeof activity.title === 'object') {
          Object.entries(activity.title).forEach(([key, value]) => {
            titleObj[key] = value as string;
          });
        }
      }

      const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
      const descriptionObj: { [key: string]: string | undefined } = {};
      if (activity.description) {
        if (typeof activity.description === 'string') {
          descriptionObj.en = activity.description;
        } else if (typeof activity.description === 'object') {
          Object.entries(activity.description).forEach(([key, value]) => {
            descriptionObj[key] = value as string;
          });
        }
      }
      const translatedDescription = getTranslation(descriptionObj, locale, 'No description available');
      const imageUrl = getAssetUrl(activity.mainImageKey);

      const firstName = activity.professionalName?.firstName || '';
      const lastName = activity.professionalName?.lastName || '';
      const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

      return {
        id: activity.id,
        title: translatedTitle,
        description: translatedDescription,
        imageUrl,
        professionalName,
        professionalId: professionalId,
        isAtHome: activity.isAtHome === null ? undefined : activity.isAtHome,
        // The professional-activities response carries `{ id, kind, label }` per
        // location (no address / meeting config). Map them through so the agenda /
        // calendar / pro-sidebar can derive a per-slot deliveryMode + physical /
        // online split from the occurrence's locationIds (see useAvailableSlotsQuery,
        // which reads loc.id + loc.kind), and so the direct-booking dropdown can
        // show the pro's venue labels. isActive is unknown here, so default true.
        locations: activity.locations?.map((l) => ({
          id: l.id,
          kind: l.kind as 'venue' | 'at_home' | 'online',
          label: l.label ?? undefined,
          isActive: true
        })),
        locationKinds: (activity as { locationKinds?: ('venue' | 'at_home' | 'online')[] }).locationKinds,
        homeRadius: (activity as any).homeRadius,
        timezone: activity.timezone ?? undefined,
        startingPrice: (activity.startingPrice as number | null) ?? null,
        completedBookingsCount: activity.completedBookingsCount || 0,
        bookingsSinceLastYear: activity.slots ? calculateBookingsSinceLastYear(activity.slots) : 0,
        location: {
          address: activity.locationAddress || 'Location not specified',
          coordinates: {
            latitude: activity.latitude || 0,
            longitude: activity.longitude || 0
          }
        },
        category: activity.categoryId,
        slots: activity.slots,
        onlineEnabled: (activity as any).onlineEnabled ?? false,
        status: activity.status,
        rating:
          activity.ratingTotal != null && activity.ratingTotal > 0
            ? {
                ratingAverage: activity.ratingAverage ?? 0,
                ratingTotal: activity.ratingTotal,
                ratingDist: [
                  activity.ratingDist1 ?? 0,
                  activity.ratingDist2 ?? 0,
                  activity.ratingDist3 ?? 0,
                  activity.ratingDist4 ?? 0,
                  activity.ratingDist5 ?? 0
                ] as [number, number, number, number, number]
              }
            : undefined
      };
    });
  } catch (error) {
    console.error(`Failed to fetch activities for professional ${professionalId}:`, error);
    return [];
  }
};

export const getUserActivities = async (params: {
  locale?: string;
  limit?: number;
  cursor?: string;
  userId: string;
}): Promise<Activity[]> => {
  const { userId, ...restParams } = params;
  return getActivitiesByProfessional(userId, restParams);
};

export const getActivity = async (
  id: string,
  locale: string = 'en',
  options?: { filterInvalidOnlinePayment?: boolean }
): Promise<Activity | null> => {
  try {
    const activityDetail: ActivityDetail = await ApiClient.getActivityById(
      id,
      options?.filterInvalidOnlinePayment !== undefined
        ? { filterInvalidOnlinePayment: options.filterInvalidOnlinePayment }
        : undefined
    );

    if (!activityDetail) {
      return null;
    }

    const titleObj: { [key: string]: string | undefined } = {};
    if (activityDetail.title) {
      if (typeof activityDetail.title === 'string') {
        titleObj.en = activityDetail.title;
      } else if (typeof activityDetail.title === 'object') {
        Object.entries(activityDetail.title).forEach(([key, value]) => {
          titleObj[key] = value as string;
        });
      }
    }

    const descriptionObj: { [key: string]: string | undefined } = {};
    if (activityDetail.description) {
      if (typeof activityDetail.description === 'string') {
        descriptionObj.en = activityDetail.description;
      } else if (typeof activityDetail.description === 'object') {
        Object.entries(activityDetail.description).forEach(([key, value]) => {
          descriptionObj[key] = value as string;
        });
      }
    }

    const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');

    const imageUrl =
      activityDetail.mediaKeys && activityDetail.mediaKeys.length > 0
        ? getAssetUrl(activityDetail.mediaKeys[0])
        : getAssetUrl('activity_placeholder.webp');

    const allMediaUrls =
      activityDetail.mediaKeys && activityDetail.mediaKeys.length > 0
        ? activityDetail.mediaKeys.map((key) => getAssetUrl(key))
        : [];

    const mediaUrls = allMediaUrls.length > 0 ? allMediaUrls.filter((url) => url !== imageUrl) : undefined;

    const firstName = activityDetail.professional?.firstName || '';
    const lastName = activityDetail.professional?.lastName || '';
    const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

    const professional: Professional | undefined = activityDetail.professional
      ? {
          id: activityDetail.professional.id,
          name: professionalName,
          profileImageUrl: (activityDetail.professional as any)?.profilePictureKey
            ? getAssetUrl((activityDetail.professional as any).profilePictureKey)
            : null,
          label: 'Professional',
          joinedYear: dateFromMixedTimestamp(
            (activityDetail.professional as any)?.joinedDate || Date.now()
          ).getFullYear(),
          bookingsCount: (activityDetail.professional as any)?.totalBookingsGiven || 0,
          activityCount: 0,
          // `shortBio` is a translation map (`{en, fr, es}`) on the API; resolve
          // it to a plain string for the locale before it reaches the render
          // tree. Passing the raw object through made the prerender throw
          // "Objects are not valid as a React child" in ProfessionalSection
          // (which renders `professional.bio` directly).
          bio: getTranslation((activityDetail.professional as any)?.shortBio, locale, '') || undefined,
          socials: undefined
        }
      : undefined;

    const translatedDescription = getTranslation(descriptionObj, locale, 'No description available');

    const rating: ActivityRatingData | undefined =
      activityDetail.ratingTotal != null && activityDetail.ratingTotal > 0
        ? {
            ratingAverage: activityDetail.ratingAverage ?? 0,
            ratingTotal: activityDetail.ratingTotal,
            ratingDist: [
              activityDetail.ratingDist1 ?? 0,
              activityDetail.ratingDist2 ?? 0,
              activityDetail.ratingDist3 ?? 0,
              activityDetail.ratingDist4 ?? 0,
              activityDetail.ratingDist5 ?? 0
            ]
          }
        : undefined;

    const result: Activity = {
      id: activityDetail.id,
      title: translatedTitle,
      description: translatedDescription,
      disclaimer:
        getTranslation((activityDetail as { disclaimer?: Record<string, string> }).disclaimer, locale, '') || undefined,
      imageUrl,
      professionalName,
      professionalId: activityDetail.professional?.id,
      professionalEmail: activityDetail.professional?.publicEmail,
      professionalPhone: (activityDetail.professional as any)?.phoneNumber,
      professional,
      isAtHome: activityDetail.isAtHome === null ? undefined : activityDetail.isAtHome,
      homeRadius: activityDetail.homeRadius,
      locations: activityDetail.locations?.map((l) => ({
        id: l.id,
        kind: l.kind as 'venue' | 'at_home' | 'online',
        label: l.label ?? undefined,
        isActive: l.isActive ?? true,
        meetingProvider: (l as { meetingProvider?: 'toptoptime' | 'byo' }).meetingProvider ?? undefined,
        meetingUrl: (l as { meetingUrl?: string }).meetingUrl ?? undefined
      })),
      locationKinds: (activityDetail as { locationKinds?: ('venue' | 'at_home' | 'online')[] }).locationKinds,
      onlineEnabled: (activityDetail.locations ?? []).some((l) => l.kind === 'online' && (l.isActive ?? true)),
      meetingProvider: (activityDetail.locations ?? []).find((l) => l.kind === 'online' && (l.isActive ?? true))
        ?.meetingProvider as 'toptoptime' | 'byo' | undefined,
      meetingUrl: (activityDetail.locations ?? []).find((l) => l.kind === 'online' && (l.isActive ?? true))
        ?.meetingUrl as string | undefined,
      timezone: activityDetail.timezone ?? undefined,
      startingPrice: activityDetail.startingPrice ?? null,
      startingPriceSchedule:
        (activityDetail as { startingPriceSchedule?: { from: string; price: number | null }[] | null })
          .startingPriceSchedule ?? null,
      completedBookingsCount: activityDetail.completedBookingsCount || 0,
      bookingsSinceLastYear: calculateBookingsSinceLastYear(activityDetail.slots),
      location: {
        address: activityDetail.locationAddress || 'Location not specified',
        coordinates: {
          latitude: activityDetail.latitude || 0,
          longitude: activityDetail.longitude || 0
        }
      },
      category: activityDetail.category?.id,
      slots: activityDetail.slots,
      status: activityDetail.status,
      scopes: (activityDetail as { scopes?: string[] }).scopes ?? [],
      rating
    };

    if (mediaUrls && mediaUrls.length > 0) {
      result.mediaUrls = mediaUrls;
    }

    if (activityDetail.mediaKeys && activityDetail.mediaKeys.length > 0) {
      result.mediaKeys = activityDetail.mediaKeys;
    }

    if (activityDetail.imageEdits) {
      result.imageEdits = activityDetail.imageEdits;
    }

    return result;
  } catch (error) {
    console.error(`Error fetching activity with ID ${id}:`, error);
    return null;
  }
};

export const getActivityForBooking = getActivity;

/**
 * Overlay an activity with the proposed data from a pending revision so the
 * preview route can show what the activity will look like once approved.
 *
 * The revision JSONB matches the fields merged at approval time
 * (see approveActivityRevision.ts). We translate the localized fields the
 * same way `getActivity` does so the visible output matches.
 */
export const applyRevisionOverlay = (
  activity: Activity,
  revision: ActivityRevision,
  locale: string = 'en'
): Activity => {
  const data = revision.data;
  const next: Activity = { ...activity };

  if (data.title) {
    next.title = getTranslation(data.title as { [k: string]: string | undefined }, locale, activity.title);
  }
  if (data.description) {
    next.description = getTranslation(
      data.description as { [k: string]: string | undefined },
      locale,
      activity.description
    );
  }
  if (data.mediaKeys && data.mediaKeys.length > 0) {
    next.mediaKeys = data.mediaKeys;
    next.imageUrl = getAssetUrl(data.mediaKeys[0]);
    const allUrls = data.mediaKeys.map((k) => getAssetUrl(k));
    next.mediaUrls = allUrls.filter((u) => u !== next.imageUrl);
  }
  if (data.isAtHome != null) {
    next.isAtHome = data.isAtHome;
  }
  if (data.homeRadius !== undefined) {
    next.homeRadius = data.homeRadius;
  }
  if (data.location) {
    next.location = {
      address: data.location.address ?? activity.location.address,
      coordinates: {
        latitude: data.location.coordinates?.latitude ?? activity.location.coordinates.latitude,
        longitude: data.location.coordinates?.longitude ?? activity.location.coordinates.longitude
      }
    };
  }
  if (data.categoryId) {
    next.category = data.categoryId;
  }

  return next;
};

export interface CreateActivityRequest {
  title: string;
  description: string;
  categoryId: string;
  professionalId?: string;
  location?: {
    address: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  homeRadius?: number;
  mainImageKey: string;
  mediaKeys: string[];
  disclaimer?: string;
  isAdminAction?: boolean;
  /** The activity's full initial location set (the create wizard's local draft).
   * When present, the server creates exactly these and skips the auto-provisioned
   * default in-person + online locations. */
  locations?: ApiActivityLocationDraft[];
}

interface UpdateActivityRequest {
  title?: string;
  description?: string;
  categoryId?: string;
  isAtHome?: boolean;
  location?: {
    address: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  homeRadius?: number;
  mainImageKey?: string;
  mediaKeys?: string[];
  /** Single-string disclaimer; '' clears it. Undefined leaves it untouched. */
  disclaimer?: string;
  status?: ActivityStatus;
  isAdminAction?: boolean;
  locations?: ApiActivityLocationDraft[];
}

export const createActivity = async (request: CreateActivityRequest, locale: string = 'en'): Promise<Activity> => {
  try {
    let professionalId = request.professionalId;
    if (!professionalId) {
      const session = await getSession();
      const userId = session?.user?.id;
      if (!userId) {
        throw new Error('Authentication error: missing user id');
      }
      professionalId = userId;
    }

    const createActivityBody: CreateActivityBody & {
      isAdminAction?: boolean;
      locations?: ApiActivityLocationDraft[];
    } = {
      title: {
        en: request.title,
        es: request.title,
        fr: request.title
      },
      description: {
        en: request.description,
        es: request.description,
        fr: request.description
      },
      categoryId: request.categoryId,
      professionalId,
      // Location kind is derived from the seed: a non-null homeRadius makes the
      // default location an at_home service area, else a venue. The inert
      // `isAtHome` hint is no longer part of the create body.
      locationAddress: request.location?.address,
      latitude: request.location?.coordinates?.latitude,
      longitude: request.location?.coordinates?.longitude,
      homeRadius: request.homeRadius,
      mainImageKey: request.mainImageKey,
      mediaKeys: request.mediaKeys,
      // Same single-string -> per-locale convention as title/description.
      ...(request.disclaimer
        ? { disclaimer: { en: request.disclaimer, es: request.disclaimer, fr: request.disclaimer } }
        : {}),
      isAdminAction: request.isAdminAction,
      ...(request.locations !== undefined ? { locations: request.locations } : {})
    };

    const createdActivity: ApiActivity = await ApiClient.createActivity(createActivityBody);

    const titleObj: { [key: string]: string | undefined } = {};
    if (createdActivity.title) {
      if (typeof createdActivity.title === 'string') {
        titleObj.en = createdActivity.title;
      } else if (typeof createdActivity.title === 'object') {
        Object.entries(createdActivity.title).forEach(([key, value]) => {
          titleObj[key] = value as string;
        });
      }
    }

    const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
    const imageUrl = getAssetUrl(createdActivity.mainImageKey);

    const descriptionObj: { [key: string]: string | undefined } = {};
    if (createdActivity.description) {
      if (typeof createdActivity.description === 'string') {
        descriptionObj.en = createdActivity.description;
      } else if (typeof createdActivity.description === 'object') {
        Object.entries(createdActivity.description).forEach(([key, value]) => {
          descriptionObj[key] = value as string;
        });
      }
    }

    const result: Activity = {
      id: createdActivity.id,
      title: translatedTitle,
      description: getTranslation(descriptionObj, locale, 'No description available'),
      imageUrl,
      professionalName: '',
      professionalId: createdActivity.professionalId,
      isAtHome: createdActivity.isAtHome === null ? undefined : createdActivity.isAtHome,
      startingPrice: createdActivity.startingPrice ?? null,
      completedBookingsCount: createdActivity.completedBookingsCount || 0,
      bookingsSinceLastYear: 0,
      location: {
        address: createdActivity.locationAddress || 'Location not specified',
        coordinates: {
          latitude: createdActivity.latitude || 0,
          longitude: createdActivity.longitude || 0
        }
      },
      category: createdActivity.categoryId,
      status: createdActivity.status
    };

    return result;
  } catch (error) {
    console.error('Failed to create activity:', error);
    // Preserve the original ApiError so [CODE]-prefixed server errors stay
    // localizable downstream (symmetric with updateActivity).
    throw error instanceof Error ? error : new Error('Failed to create activity. Please try again.');
  }
};

export const updateActivity = async (
  activityId: string,
  request: UpdateActivityRequest,
  locale: string = 'en'
): Promise<Activity> => {
  try {
    const updateActivityBody: UpdateActivityBody & { isAdminAction?: boolean } = {};

    if (request.isAdminAction) {
      updateActivityBody.isAdminAction = true;
    }

    if (request.title !== undefined) {
      updateActivityBody.title = {
        en: request.title,
        es: request.title,
        fr: request.title
      };
    }

    if (request.description !== undefined) {
      updateActivityBody.description = {
        en: request.description,
        es: request.description,
        fr: request.description
      };
    }

    if (request.categoryId !== undefined) {
      updateActivityBody.categoryId = request.categoryId;
    }

    // Activity-level location fields (isAtHome / address / coords / homeRadius) are
    // no longer part of the update body; locations are edited through the
    // `locations` reconcile array below.

    if (request.mainImageKey !== undefined) {
      updateActivityBody.mainImageKey = request.mainImageKey;
    }

    if (request.mediaKeys !== undefined) {
      updateActivityBody.mediaKeys = request.mediaKeys;
    }

    if (request.status !== undefined) {
      updateActivityBody.status = request.status;
    }

    if (request.locations !== undefined) {
      updateActivityBody.locations = request.locations;
    }

    if (request.disclaimer !== undefined) {
      // Same single-string -> per-locale convention as title/description; empty
      // strings are valid per the API schema and act as "cleared".
      updateActivityBody.disclaimer = {
        en: request.disclaimer,
        es: request.disclaimer,
        fr: request.disclaimer
      };
    }

    const updatedActivity: ApiActivity = await ApiClient.updateActivity(activityId, updateActivityBody);

    const titleObj: { [key: string]: string | undefined } = {};
    if (updatedActivity.title) {
      if (typeof updatedActivity.title === 'string') {
        titleObj.en = updatedActivity.title;
      } else if (typeof updatedActivity.title === 'object') {
        Object.entries(updatedActivity.title).forEach(([key, value]) => {
          titleObj[key] = value as string;
        });
      }
    }

    const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
    const imageUrl = getAssetUrl(updatedActivity.mainImageKey);

    const descriptionObj: { [key: string]: string | undefined } = {};
    if (updatedActivity.description) {
      if (typeof updatedActivity.description === 'string') {
        descriptionObj.en = updatedActivity.description;
      } else if (typeof updatedActivity.description === 'object') {
        Object.entries(updatedActivity.description).forEach(([key, value]) => {
          descriptionObj[key] = value as string;
        });
      }
    }

    const result: Activity = {
      id: updatedActivity.id,
      title: translatedTitle,
      description: getTranslation(descriptionObj, locale, 'No description available'),
      imageUrl,
      professionalName: '',
      professionalId: updatedActivity.professionalId,
      isAtHome: updatedActivity.isAtHome === null ? undefined : updatedActivity.isAtHome,
      startingPrice: updatedActivity.startingPrice ?? null,
      completedBookingsCount: updatedActivity.completedBookingsCount || 0,
      bookingsSinceLastYear: 0,
      location: {
        address: updatedActivity.locationAddress || 'Location not specified',
        coordinates: {
          latitude: updatedActivity.latitude || 0,
          longitude: updatedActivity.longitude || 0
        }
      },
      category: updatedActivity.categoryId,
      status: updatedActivity.status
    };

    return result;
  } catch (error) {
    console.error('Failed to update activity:', error);
    // Preserve the original ApiError (message + statusCode): the server's
    // [CODE]-prefixed reconcile/validation errors (e.g. [DELETE_BOOKINGS],
    // [ONLINE_CONFLICT]) must reach ActivityForm so it can localize them, instead
    // of being flattened into a generic, unactionable message.
    throw error instanceof Error ? error : new Error('Failed to update activity. Please try again.');
  }
};

// Activity locations are managed through the activity update (`locations` field),
// reconciled server-side; there are no per-location service helpers on the website.

// Admin activities interface
export interface AdminActivity {
  id: string;
  title: string;
  imageUrl: string;
  professionalName: string;
  professionalId?: string;
  categoryId?: string;
  isAtHome?: boolean;
  startingPrice: number;
  location: {
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  status: ActivityStatus;
  /** Whether this activity has a pending revision awaiting admin review */
  hasPendingRevision?: boolean;
  /** Number of still-bookable future slots (past slots excluded) */
  futureSlotsCount: number;
  /** Number of upcoming (non-cancelled, session not yet ended) bookings */
  upcomingBookingsCount: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface GetAdminActivitiesResult {
  activities: AdminActivity[];
  total: number;
}

/**
 * Get all activities for admin - includes all activities regardless of slot status.
 * Uses the dedicated admin endpoint that doesn't filter out activities without slots.
 */
export const getActivitiesAdmin = async (
  params: {
    locale?: string;
    statusFilter?: GetActivitiesAdminStatusFilter;
    /**
     * Filter by effective audience (activity.scopes ∪ owner.user.groups). Pass
     * an array of group IDs (any-match), or include `__none__` to also match
     * activities with no scopes and an ungrouped owner.
     */
    groupIds?: string[];
  } = {}
): Promise<GetAdminActivitiesResult> => {
  const { locale = 'en', statusFilter, groupIds } = params;

  try {
    const groupId = groupIds && groupIds.length > 0 ? groupIds.join(',') : undefined;
    const apiResponse = await ApiClient.getActivitiesAdmin({ statusFilter, ...(groupId ? { groupId } : {}) } as any);

    if (!apiResponse || typeof apiResponse !== 'object') {
      return { activities: [], total: 0 };
    }

    const activitiesData = Array.isArray(apiResponse) ? apiResponse : (apiResponse as any).data || [];

    const total = (apiResponse as any).total ?? activitiesData.length;

    const activities = activitiesData.map((activity: any) => {
      const titleObj: { [key: string]: string | undefined } = {};
      if (activity.title) {
        if (typeof activity.title === 'string') {
          titleObj.en = activity.title;
        } else if (typeof activity.title === 'object') {
          Object.entries(activity.title).forEach(([key, value]) => {
            titleObj[key] = value as string;
          });
        }
      }

      const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
      const imageUrl = getAssetUrl(activity.mainImageKey);

      const firstName = activity.professionalName?.firstName || '';
      const lastName = activity.professionalName?.lastName || '';
      const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

      return {
        id: activity.id,
        title: translatedTitle,
        imageUrl,
        professionalName,
        professionalId: activity.professionalId,
        categoryId: activity.categoryId,
        isAtHome: activity.isAtHome === null ? undefined : activity.isAtHome,
        // TODO(tz): expose timezone on getActivitiesAdmin response to enable browser-time conversion here
        startingPrice: activity.startingPrice ?? 0,
        location: {
          address: activity.locationAddress || 'Location not specified',
          coordinates: {
            latitude: activity.latitude || 0,
            longitude: activity.longitude || 0
          }
        },
        status: activity.status,
        hasPendingRevision: !!activity.hasPendingRevision,
        futureSlotsCount: activity.futureSlotsCount ?? 0,
        upcomingBookingsCount: activity.upcomingBookingsCount ?? 0,
        createdAt: activity.createdAt,
        updatedAt: activity.updatedAt
      };
    });

    return { activities, total };
  } catch (error) {
    console.error('Failed to fetch admin activities:', error);
    return { activities: [], total: 0 };
  }
};

// ---------------------------------------------------------------------------
// Activity Revisions
// ---------------------------------------------------------------------------

export interface ActivityRevision {
  id: string;
  activityId: string;
  submittedBy: string;
  status: 'pending' | 'approved' | 'declined';
  data: {
    title?: { en?: string; fr?: string; es?: string };
    description?: { en?: string; fr?: string; es?: string };
    mainImageKey?: string;
    mediaKeys?: string[];
    location?: {
      address?: string;
      coordinates?: {
        latitude?: number;
        longitude?: number;
      };
    };
    homeRadius?: number;
    isAtHome?: boolean | null;
    disclaimer?: { en?: string; fr?: string; es?: string };
    categoryId?: string;
  };
  rejectionReason?: string;
  rejectionSections?: string[];
  reviewedBy?: string;
  reviewedAt?: number;
  createdAt: number;
  updatedAt: number;
}

/** Get all revisions for an activity (admin & professional owner). */
export const getActivityRevisions = async (activityId: string): Promise<ActivityRevision[]> => {
  try {
    return await browserFetch<ActivityRevision[]>(`/activities/${activityId}/revisions`, {
      method: 'GET'
    });
  } catch (error) {
    console.error('Failed to fetch activity revisions:', error);
    return [];
  }
};

export interface CacheInfo {
  /** Epoch ms of the most recent CDN invalidation; null if never / not-owner. */
  cacheInvalidatedAt: number | null;
}

/**
 * Fetch the timestamp of the most recent static-content invalidation for this
 * activity. The backend returns `{ cacheInvalidatedAt: null }` to non-owners
 * (rather than 403), so the call is safe to make speculatively from the
 * activity detail page.
 */
export const getActivityCacheInfo = async (activityId: string): Promise<CacheInfo> => {
  try {
    return await browserFetch<CacheInfo>(`/activities/${activityId}/cache-info`, { method: 'GET' });
  } catch (error) {
    console.error('Failed to fetch activity cache info:', error);
    return { cacheInvalidatedAt: null };
  }
};

export type { ActivityAvailabilitySummary };

/**
 * Live availability signals for the public activity-detail island (from-price,
 * slot types, accepted payments, online flag, venue pins + privacy-fuzzed
 * at-home area). Computed server-side from the activity's future-open
 * occurrences. Fetched on the client on mount; the detail page itself stays
 * SSR-cached for the static catalog content.
 */
export const getActivityAvailabilitySummary = async (activityId: string): Promise<ActivityAvailabilitySummary> => {
  return ApiClient.getActivityAvailabilitySummary(activityId);
};

export interface ImproveActivityDescriptionResult {
  paragraphs: string[];
}

export interface ImproveActivityDescriptionParams {
  paragraphs: string[];
  locale?: string;
  activityTitle?: string;
  categoryName?: string;
  professionalName?: string;
  professionalBio?: string;
}

/** Send current description paragraphs to the AI and receive an improved version. */
export const improveActivityDescription = async (
  params: ImproveActivityDescriptionParams
): Promise<ImproveActivityDescriptionResult> => {
  return ApiClient.improveActivityDescription({
    paragraphs: params.paragraphs,
    // useLocale() returns a wide string; the API only accepts en/fr/es.
    // Send only when it's one of the supported values, otherwise omit and
    // let the AI auto-detect the input language.
    locale: isSupportedAiLocale(params.locale) ? (params.locale as ImproveActivityDescriptionBodyLocale) : undefined,
    activityTitle: params.activityTitle,
    categoryName: params.categoryName,
    professionalName: params.professionalName,
    professionalBio: params.professionalBio
  });
};

function isSupportedAiLocale(locale: string | undefined): boolean {
  return locale === 'en' || locale === 'fr' || locale === 'es';
}

/** Admin: approve the pending revision for an activity. */
export const approveActivityRevision = async (activityId: string): Promise<void> => {
  await browserFetch(`/activities/${activityId}/revisions/approve`, {
    method: 'POST',
    body: JSON.stringify({ activityId })
  });
};

/** Admin: decline the pending revision for an activity with a reason. */
export const declineActivityRevision = async (
  activityId: string,
  rejectionReason: string,
  rejectionSections?: string[]
): Promise<void> => {
  await browserFetch(`/activities/${activityId}/revisions/decline`, {
    method: 'POST',
    body: JSON.stringify({ activityId, rejectionReason, rejectionSections })
  });
};
